import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Avatar,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import LinkIcon from "@mui/icons-material/Link";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { QRCodeCanvas } from "qrcode.react";
import {
  performersSliceSelector,
  fetchPerformers,
  deletePerformer,
  sendPerformerEditLinkEmail,
  createPerformerEditLink,
  applyPerformerDraft,
  dismissPerformerDraft,
} from "../../../../../redux/features/performersSlice";
import ConfirmationDialog from "../../../../ConfirmationDialog";
import LoadingOverlay from "../../../../LoadingOverlay";
import { LINK_PRESETS, linkToPresetValue } from "../PerformerForm/linkPresets";

const PerformerQRDialog = ({ open, url, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>
      Edit link – scan to open
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: "absolute", right: 8, top: 8 }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
      {url && <QRCodeCanvas value={url} size={200} />}
      <Typography variant="body2" color="text.secondary" textAlign="center">
        Performer can scan this to open their edit page
      </Typography>
    </DialogContent>
  </Dialog>
);

const PerformerReviewModal = ({
  open,
  performer,
  onClose,
  onApprove,
  onDismiss,
  loading,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = useState(1);

  useEffect(() => {
    if (open) setTab(1);
  }, [open]);

  if (!performer?.pendingDraft) return null;
  const current = performer;
  const submitted = performer.pendingDraft;

  const rowSx = { display: "flex", gap: 2, alignItems: "flex-start", mb: 2 };
  const labelSx = { flex: "0 0 88px", typography: "body2", color: "text.secondary", fontWeight: 500, pt: 0.25 };
  const Field = ({ label, value }) => (
    <Box sx={rowSx}>
      <Typography variant="body2" sx={labelSx}>{label}</Typography>
      <Typography variant="body2" sx={{ flex: 1, whiteSpace: "pre-wrap", minWidth: 0 }}>{value || "—"}</Typography>
    </Box>
  );
  const HeadshotField = ({ label, headshotUrl }) => (
    <Box sx={rowSx}>
      <Typography variant="body2" sx={labelSx}>{label}</Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {headshotUrl ? (
          <Box
            component="img"
            src={headshotUrl}
            alt="Headshot"
            sx={{ width: 80, height: 80, objectFit: "cover", borderRadius: "50%" }}
          />
        ) : (
          <Typography variant="body2">—</Typography>
        )}
      </Box>
    </Box>
  );
  const LinksIconsField = ({ label, links }) => {
    const linkList = links || [];
    return (
      <Box sx={rowSx}>
        <Typography variant="body2" sx={labelSx}>{label}</Typography>
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", minWidth: 0 }}>
          {linkList.length === 0 ? (
            <Typography variant="body2">—</Typography>
          ) : (
            linkList.map((link, i) => {
              if (!link?.url?.trim()) return null;
              const { presetId, value } = linkToPresetValue(link);
              const preset = LINK_PRESETS.find((p) => p.id === presetId);
              const Logo = preset?.Logo;
              const tooltipText = preset?.inputType === "username" && value ? value : (link.label || link.url || "Link");
              if (!Logo) return null;
              return (
                <Tooltip key={i} title={tooltipText}>
                  <IconButton
                    component="a"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    aria-label={preset?.label || link.label}
                    sx={{ color: "text.secondary", p: 0.5 }}
                  >
                    <Logo sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              );
            })
          )}
        </Box>
      </Box>
    );
  };
  const currentPanel = (
    <>
      <HeadshotField label="Headshot" headshotUrl={current.headshotUrl} />
      <Field label="Name" value={current.name} />
      <Field label="Email" value={current.email} />
      <LinksIconsField label="Links" links={current.links} />
      <Field label="Bio" value={current.bio} />
      <Field label="Credits" value={current.credits} />
    </>
  );
  const submittedPanel = (
    <>
      <HeadshotField label="Headshot" headshotUrl={submitted.headshotUrl} />
      <Field label="Name" value={submitted.name} />
      <Field label="Email" value={submitted.email} />
      <LinksIconsField label="Links" links={submitted.links} />
      <Field label="Bio" value={submitted.bio} />
      <Field label="Credits" value={submitted.credits} />
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: (theme) => ({
          boxSizing: "border-box",
          width: "100%",
          maxWidth: `min(100vw, ${theme.breakpoints.values.sm}px) !important`,
          overflow: "hidden",
        }),
      }}
    >
      <DialogTitle sx={{ pr: 6, boxSizing: "border-box" }}>
        Review updates
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          boxSizing: "border-box",
          p: 0,
          width: "100%",
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          aria-label="Current vs submitted profile"
          sx={{
            boxSizing: "border-box",
            borderBottom: 1,
            borderColor: "divider",
            minHeight: 48,
            minWidth: 0,
            width: "100%",
            "& .MuiTabs-scroller": { overflow: "hidden !important" },
            "& .MuiTabs-flexContainer": { minWidth: 0 },
            "& .MuiTab-root": { minWidth: 0 },
          }}
        >
          <Tab label="Current" id="review-tab-current" aria-controls="review-panel-current" />
          <Tab label="Submitted for review" id="review-tab-submitted" aria-controls="review-panel-submitted" />
        </Tabs>
        <Box sx={{ boxSizing: "border-box", flex: 1, overflow: "auto", minWidth: 0, width: "100%", px: 2, py: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, overflowWrap: "break-word", maxWidth: "100%" }}
          >
            Compare current profile with the submitted changes. Approve to publish, or dismiss to keep current.
          </Typography>
          <div
            role="tabpanel"
            id="review-panel-current"
            aria-labelledby="review-tab-current"
            hidden={tab !== 0}
          >
            {currentPanel}
          </div>
          <div
            role="tabpanel"
            id="review-panel-submitted"
            aria-labelledby="review-tab-submitted"
            hidden={tab !== 1}
          >
            {submittedPanel}
          </div>
        </Box>
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", p: 2, borderTop: 1, borderColor: "divider", minWidth: 0 }}>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="outlined" color="inherit" onClick={onDismiss} disabled={loading}>
            Reject
          </Button>
          <Button variant="contained" onClick={onApprove} disabled={loading}>
            {loading ? "…" : "Approve"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const PerformersList = () => {
  const { organization } = useOutletContext();
  const navigate = useNavigate();
  const { id: organizationId } = organization || {};
  const dispatch = useDispatch();
  const performersState = useSelector((state) => performersSliceSelector(state));
  const { performers = {}, performersLoading, performersFetched } = performersState || {};
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [performerToDelete, setPerformerToDelete] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuPerformer, setMenuPerformer] = useState(null);
  const [snackMessage, setSnackMessage] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewPerformer, setReviewPerformer] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [menuOpenUpward, setMenuOpenUpward] = useState(false);

  useEffect(() => {
    if (!menuAnchor) return;
    const rect = menuAnchor.getBoundingClientRect();
    const estimatedMenuHeight = 220;
    const spaceBelow = window.innerHeight - rect.bottom;
    setMenuOpenUpward(spaceBelow < estimatedMenuHeight);
  }, [menuAnchor]);

  if (!organization) {
    return null;
  }

  const performersList = Object.keys(performers)
    .map((id) => performers[id])
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));

  const searchLower = (searchQuery || "").trim().toLowerCase();
  const filteredList = searchLower
    ? performersList.filter((p) => {
        const name = (p.name || "").toLowerCase();
        const email = (p.email || "").toLowerCase();
        return name.includes(searchLower) || email.includes(searchLower);
      })
    : performersList;

  const hasPendingUpdate = (p) => Boolean(p.pendingDraft != null && Object.keys(p.pendingDraft).length > 0);
  const pendingUpdatesList = filteredList.filter(hasPendingUpdate);
  const restList = filteredList.filter((p) => !hasPendingUpdate(p));
  const pendingCount = pendingUpdatesList.length;

  const handleAddPerformer = () => {
    navigate(`/organizations/${organizationId}/performers/new`);
  };

  const handleEditPerformer = (performer) => {
    setMenuAnchor(null);
    setMenuPerformer(null);
    navigate(`/organizations/${organizationId}/performers/${performer.id}/edit`);
  };

  const handleCardClick = (performer) => {
    if (hasPendingUpdate(performer)) {
      setReviewPerformer(performer);
      setReviewModalOpen(true);
    } else {
      handleEditPerformer(performer);
    }
  };

  const handleReviewApprove = async () => {
    if (!reviewPerformer?.id) return;
    setReviewLoading(true);
    try {
      await dispatch(
        applyPerformerDraft({ organizationId, performerId: reviewPerformer.id }),
      ).unwrap();
      setSnackMessage("Updates approved and published");
      setReviewModalOpen(false);
      setReviewPerformer(null);
      dispatch(fetchPerformers({ organizationId }));
    } catch (err) {
      setSnackMessage(err?.message || "Failed to approve");
    } finally {
      setReviewLoading(false);
    }
  };

  const handleReviewDismiss = async () => {
    if (!reviewPerformer?.id) return;
    setReviewLoading(true);
    try {
      await dispatch(
        dismissPerformerDraft({ organizationId, performerId: reviewPerformer.id }),
      ).unwrap();
      setSnackMessage("Updates dismissed");
      setReviewModalOpen(false);
      setReviewPerformer(null);
      dispatch(fetchPerformers({ organizationId }));
    } catch (err) {
      setSnackMessage(err?.message || "Failed to dismiss");
    } finally {
      setReviewLoading(false);
    }
  };

  const handleDeleteClick = (performer) => {
    setPerformerToDelete(performer);
    setDeleteConfirmOpen(true);
    setMenuAnchor(null);
    setMenuPerformer(null);
  };

  const handleDeleteConfirm = async () => {
    setDeleteConfirmOpen(false);
    const toDelete = performerToDelete;
    setPerformerToDelete(null);
    if (!toDelete?.id) return;
    setActionLoading(true);
    try {
      await dispatch(
        deletePerformer({
          organizationId,
          performerId: toDelete.id,
        }),
      );
      dispatch(fetchPerformers({ organizationId }));
    } finally {
      setActionLoading(false);
    }
  };

  const handleEmailLink = async (performer) => {
    setMenuAnchor(null);
    setMenuPerformer(null);
    setActionLoading(true);
    try {
      await dispatch(
        sendPerformerEditLinkEmail({
          organizationId,
          performerId: performer.id,
        }),
      ).unwrap();
      setSnackMessage("Edit link sent to " + (performer.email || "performer"));
    } catch (err) {
      setSnackMessage(err?.message || "Failed to send email");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyLink = async (performer) => {
    setMenuAnchor(null);
    setMenuPerformer(null);
    setActionLoading(true);
    try {
      const result = await dispatch(
        createPerformerEditLink({
          organizationId,
          performerId: performer.id,
        }),
      ).unwrap();
      const url = result?.editUrl;
      if (url && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setSnackMessage("Edit link copied to clipboard");
      } else {
        setSnackMessage("Edit link ready");
      }
    } catch (err) {
      setSnackMessage(err?.message || "Failed to create link");
    } finally {
      setActionLoading(false);
    }
  };

  const handleShowQR = async (performer) => {
    setMenuAnchor(null);
    setMenuPerformer(null);
    setActionLoading(true);
    try {
      const result = await dispatch(
        createPerformerEditLink({
          organizationId,
          performerId: performer.id,
        }),
      ).unwrap();
      const url = result?.editUrl;
      if (url) {
        setQrUrl(url);
        setQrDialogOpen(true);
      }
    } catch (err) {
      setSnackMessage(err?.message || "Failed to create link");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box sx={{ paddingBottom: "20px" }}>
      <LoadingOverlay show={actionLoading || reviewLoading} />
      <ConfirmationDialog
        open={deleteConfirmOpen}
        title="Delete performer"
        message="Are you sure you want to remove this performer from the roster? They will be removed from any event or session assignments."
        handleConfirm={handleDeleteConfirm}
        handleCancel={() => {
          setDeleteConfirmOpen(false);
          setPerformerToDelete(null);
        }}
        okButtonText="Delete"
      />
      <PerformerQRDialog
        open={qrDialogOpen}
        url={qrUrl}
        onClose={() => {
          setQrDialogOpen(false);
          setQrUrl("");
        }}
      />
      <PerformerReviewModal
        open={reviewModalOpen}
        performer={reviewPerformer}
        onClose={() => {
          if (!reviewLoading) {
            setReviewModalOpen(false);
            setReviewPerformer(null);
          }
        }}
        onApprove={handleReviewApprove}
        onDismiss={handleReviewDismiss}
        loading={reviewLoading}
      />
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setMenuPerformer(null);
        }}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: menuOpenUpward ? "top" : "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: menuOpenUpward ? "bottom" : "top",
          horizontal: "right",
        }}
        sx={menuOpenUpward ? { mb: 1 } : { mt: 1 }}
      >
        <Tooltip
          title={
            menuPerformer?.email?.trim()
              ? ""
              : "Add an email address to this performer to send an edit link."
          }
          placement="left"
        >
          <span style={{ display: "inline-flex", width: "100%" }}>
            <MenuItem
              disabled={!menuPerformer?.email?.trim()}
              onClick={() => handleEmailLink(menuPerformer)}
            >
              <EmailIcon fontSize="small" sx={{ mr: 1 }} />
              Email link to performer
            </MenuItem>
          </span>
        </Tooltip>
        <MenuItem onClick={() => handleCopyLink(menuPerformer)}>
          <LinkIcon fontSize="small" sx={{ mr: 1 }} />
          Copy edit link
        </MenuItem>
        <MenuItem onClick={() => handleShowQR(menuPerformer)}>
          <QrCode2Icon fontSize="small" sx={{ mr: 1 }} />
          Show QR code
        </MenuItem>
        <MenuItem
          onClick={() => handleDeleteClick(menuPerformer)}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
      <Box display="flex" alignItems="center" paddingTop="20px">
        <Typography variant="h2" flexGrow={1}>
          Performers
        </Typography>
        <Button onClick={handleAddPerformer}>Add performer</Button>
      </Box>
      <Typography color="text.secondary" sx={{ marginTop: 1 }}>
        Manage your roster. Assign performers to events and sessions from each event.
      </Typography>
      {performersFetched && performersList.length > 0 && (
        <TextField
          size="small"
          placeholder="Search performers…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => setSearchQuery("")}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ marginTop: 2, maxWidth: 320 }}
        />
      )}
      {!performersLoading && performersFetched && pendingCount > 0 && (
        <Typography variant="subtitle1" color="error" fontWeight={600} sx={{ marginTop: 2 }}>
          {pendingCount} pending {pendingCount === 1 ? "review" : "review"}
        </Typography>
      )}
      <Box marginTop="20px" display="flex" flexDirection="column" gap="20px">
        {performersLoading && !performersFetched && (
          <Typography color="text.secondary">Loading…</Typography>
        )}
        {!performersLoading && performersFetched && performersList.length === 0 && (
          <Typography color="text.secondary">
            No performers yet. Add one to get started.
          </Typography>
        )}
        {!performersLoading && performersFetched && performersList.length > 0 && filteredList.length === 0 && (
          <Typography color="text.secondary">
            No performers match your search.
          </Typography>
        )}
        {!performersLoading && performersFetched && filteredList.length > 0 &&
          [...pendingUpdatesList, ...restList].map((performer) => {
            const displayName = performer.name || "Unnamed";
            const isPending = hasPendingUpdate(performer);
            return (
              <Paper
                key={performer.id}
                elevation={2}
                sx={{
                  padding: 2,
                  cursor: "pointer",
                  ...(isPending && { borderLeft: "4px solid", borderLeftColor: "error.main" }),
                }}
                onClick={() => handleCardClick(performer)}
              >
                <Grid container alignItems="center" spacing={2}>
                  <Grid item>
                    <Avatar
                      src={performer.headshotUrl}
                      sx={{ width: 56, height: 56 }}
                    >
                      <PersonIcon />
                    </Avatar>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="subtitle1">{displayName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {performer.email || "No email"}
                    </Typography>
                    {isPending && (
                      <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
                        Pending review
                      </Typography>
                    )}
                  </Grid>
                  {(performer.links || []).length > 0 && (
                    <Grid item onClick={(e) => e.stopPropagation()}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {(performer.links || []).map((link, i) => {
                          const { presetId, value } = linkToPresetValue(link);
                          const preset = LINK_PRESETS.find((p) => p.id === presetId);
                          const Logo = preset?.Logo;
                          const tooltipText = preset?.inputType === "username" && value ? value : (link.label || link.url || "Link");
                          if (!link.url?.trim() || !Logo) return null;
                          return (
                            <Tooltip key={i} title={tooltipText}>
                              <IconButton
                                component="a"
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                                aria-label={preset?.label || link.label}
                                sx={{ color: "text.secondary", p: 0.5 }}
                              >
                                <Logo sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </Grid>
                  )}
                  <Grid item>
                    <Tooltip title="Actions">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuAnchor(e.currentTarget);
                          setMenuPerformer(performer);
                        }}
                        aria-label="Actions"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Paper>
            );
          })}
      </Box>
      <Snackbar
        open={Boolean(snackMessage)}
        autoHideDuration={4000}
        onClose={() => setSnackMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackMessage(null)}
          severity={snackMessage?.startsWith("Edit link") ? "success" : "info"}
          sx={{ width: "100%" }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PerformersList;
